import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Sparkles } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useJobProfileStore } from '@/stores/jobProfileStore';
import type { ParsedJobData } from '@/types/jobProfile';

interface UrlParserProps {
 onParsed: (data: ParsedJobData, url: string) => void;
}

export function UrlParser({ onParsed }: UrlParserProps) {
 const [url, setUrl] = useState('');
 const { parseJobUrl, isLoading, error: storeError } = useJobProfileStore();
 const [localError, setLocalError] = useState<string | null>(null);
 const { toast } = useToast();

 const handleParseUrl = async () => {
  if (!url.trim()) {
   setLocalError('Please enter a job posting URL');
   return;
  }
  setLocalError(null);
  try {
   const data = await parseJobUrl(url);
   toast({
    title: 'Success!',
    description: 'Job posting parsed successfully.',
   });
   onParsed(data, url);
  } catch (error) {
   console.error('Error parsing job URL:', error);
  }
 };

 const handleKeyPress = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' && !isLoading) handleParseUrl();
 };

 const currentError = localError || storeError;

 return (
  <div className='space-y-4'>
   <div className='space-y-2'>
    <Label
     htmlFor='job-url'
     className='font-mono uppercase text-xs text-muted-foreground'
    >
     Target_Job_Posting_URL
    </Label>
    <div className='flex space-x-2'>
     <div className='flex-1'>
      <Input
       id='job-url'
       type='url'
       placeholder='https://jobs.company.com/position/123'
       value={url}
       onChange={(e) => setUrl(e.target.value)}
       onKeyPress={handleKeyPress}
       disabled={isLoading}
       className={`rounded-none font-mono bg-secondary/5 border-border focus:border-primary/50 text-sm ${
        currentError ? 'border-red-500' : ''
       }`}
      />
     </div>
     <Button
      onClick={handleParseUrl}
      disabled={isLoading || !url.trim()}
      className='min-w-[140px] rounded-none font-mono uppercase tracking-wider'
     >
      {isLoading ? (
       <>
        <Loader2 className='w-4 h-4 mr-2 animate-spin' />
        PROCESSING...
       </>
      ) : (
       <>
        <Sparkles className='w-4 h-4 mr-2' />
        PARSE_URL
       </>
      )}
     </Button>
    </div>
   </div>
   {currentError && (
    <Alert variant='destructive'>
     <AlertDescription>{currentError}</AlertDescription>
    </Alert>
   )}
  </div>
 );
}
